import { css } from '@emotion/react';
import { Spin, Button } from 'antd';
import React, { type CSSProperties } from 'react';
import type { Env0Theme } from 'constants/themes.constants';
import styled from 'types/theme.types';
import { Link, type LinkProps } from 'react-router-dom';
import { LoadingOutlined } from '@ant-design/icons';
import theme from 'constants/themes.constants';

export type ButtonSize = 'small' | 'large';
export type ButtonType = 'primary' | 'default' | 'danger' | 'link';

export interface CustomButtonProps {
  id?: string;
  type?: ButtonType;
  shape?: 'round' | 'circle';
  size?: ButtonSize;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  href?: string;
  isExternalLink?: boolean;
  isLoading?: boolean;
  addPlus?: boolean;
  block?: boolean;
  flatBottom?: boolean;
  style?: CSSProperties;
  className?: string;
  invertColor?: boolean;
  'data-e2e'?: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
}

const CustomButton = ({
  type = 'default',
  onClick,
  children,
  href,
  isExternalLink,
  isLoading = false,
  addPlus = false,
  ...properties
}: CustomButtonProps) => {
  const onClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!href) event.preventDefault();
    onClick?.(event);
  };

  const loaderColor = type === 'default' ? theme.primaryBlack : theme.primaryWhite;
  const loader = <LoadingOutlined style={{ fontSize: 18, color: loaderColor }} spin />;

  const styledButton = (
    <StyledButton onClick={onClickHandler} type={type} role={href ? 'link' : 'button'} {...properties}>
      {addPlus && !isLoading && <span>+&nbsp;</span>}
      {isLoading ? <Spin style={{ height: 18 }} size="small" indicator={loader} /> : children}
    </StyledButton>
  );

  if (href) {
    return isExternalLink ? (
      <a href={href} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
        {styledButton}
      </a>
    ) : (
      <StyledLink
        to={href}
        onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => e.stopPropagation()}
        block={properties.block}>
        {styledButton}
      </StyledLink>
    );
  } else {
    return styledButton;
  }
};

export const StyledLink = styled(({ block, ...props }: LinkProps & { block?: boolean }) => <Link {...props} />)`
  border-radius: 4px;
  ${({ block }) => (block ? 'width: 100%;' : '')}
`;

const dangerCss = (theme: Env0Theme) => css`
  &:disabled {
    background-color: ${theme.primaryDisabledGray} !important;
    border: 1px solid ${theme.primaryDisabledGray} !important;
    color: ${theme.primaryWhite} !important;
    cursor: not-allowed;
  }
`;

const defaultCss = (theme: Env0Theme) => css`
  &:disabled {
    background-color: ${theme.primaryWhite} !important;
    border: 1px solid ${theme.secondaryDisabledGray} !important;
    color: ${theme.secondaryDisabledGray} !important;
    cursor: not-allowed;
  }
`;

const primaryCss = (theme: Env0Theme) => css`
  &:disabled {
    background-color: ${theme.primaryDisabledGray} !important;
    border: 1px solid ${theme.primaryDisabledBorder} !important;
    color: ${theme.primaryDisabledText} !important;
    cursor: not-allowed;
  }
`;

const smallSizeCss = css`
  font-size: 12px !important;
  min-width: 120px !important;
  padding: 5px 10px !important;
`;

const defaultSizeCss = css`
  font-size: 13px !important;
  min-width: 130px !important;
  padding: 5px 25px !important;
`;

const largeSizeCss = css`
  font-size: 15px !important;
  min-width: 170px !important;
  padding: 10px 25px !important;
`;

const sizeStyleMap: Record<ButtonSize | 'default', any> = {
  small: smallSizeCss,
  default: defaultSizeCss,
  large: largeSizeCss
};

const getTypeStyleMap = (theme: Env0Theme): Record<ButtonType, any> => ({
  danger: dangerCss(theme),
  primary: primaryCss(theme),
  default: defaultCss(theme),
  link: null
});

type ButtonExtendedType = CustomButtonProps & { role: string };

const StyledButton = styled<React.ComponentType<ButtonExtendedType>>(
  ({ type, ...props }) => (
    <Button type={type === 'danger' ? 'primary' : type} danger={type === 'danger'} {...(props as any)} />
  ),
  {
    shouldForwardProp: propName => propName !== 'flatBottom' && propName !== 'invertColor'
  }
)`
  ${props => sizeStyleMap[props.size || 'default']}
  ${props => getTypeStyleMap(props.theme)[props.type || 'default']}

  &:hover:enabled {
    ${(props: ButtonExtendedType) => !props.disabled && 'box-shadow: 0px 1px 3px 0px rgba(0,0,0,0.3) !important;'}
  }

  .ant-spin-dot-item {
    background-color: ${props => (props.invertColor ? props.theme.primaryBlack : props.theme.primaryWhite)};
  }

  ${(props: ButtonExtendedType) =>
    props.flatBottom &&
    `
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
  `}
`;

/**
 * A Fix
 * Mouse events don't trigger at disabled button in Chrome
 *
 * This fixes the scenario when we wrap a disabled button with a Tooltip.
 * Without this fix the Tooltip message will not disappear "onmouseleave"
 *
 * relevant snippet: https://github.com/ant-design/ant-design/pull/4865/files#diff-186839a30bf8b9d67a4b10bf7c091d5f
 */
CustomButton.__ANT_BUTTON = true;

export default CustomButton;

/*
 * This version of the button is used both in RunFromHereButton and AiAnalysisButton
 * It's not another button type with its own overrides because:
 * 1. Unlike other colors, we're not using an existing color schema of antd and overriding, it's a complete override
 * 2. We did some alignment changes for the icon and text in RunFromHere, which currently do not exist in the common button
 *    We'd need to see later if we can / should have the common button do the same
 */
export const BlueButton = styled(CustomButton)`
  &:active,
  &:hover:enabled {
    background-color: ${({ theme }) => theme.tertiaryGray} !important;
    border: 1px solid ${({ theme }) => theme.tertiaryGray} !important;
    color: ${({ theme }) => theme.whiteColor} !important;
  }

  &:disabled {
    background-color: ${({ theme }) => theme.primaryDisabledGray} !important;
    border: 1px solid ${({ theme }) => theme.primaryDisabledBorder} !important;
    color: ${({ theme }) => theme.primaryDisabledText} !important;
    cursor: not-allowed;
  }

  background-color: ${({ theme }) => theme.tertiaryGray};
  border: 1px solid ${({ theme }) => theme.tertiaryGray} !important;
  color: ${({ theme }) => theme.whiteColor};

  // for some reason the icon and text are not centered by <Button/>, so I needed to override it
  display: inline-flex;
  align-items: center;
  justify-content: center;
  & span:nth-child(2) {
    margin-bottom: -2px;
  }
`;
